import React from 'react';
import { IUser } from '@tymely/atoms';
import { usePersistentEvent, useSubscription } from '@tymely/services';

import { useUser } from './auth.services';

export const useViewEvent = (channel: string | null, event: string) => {
    const user = useUser();
    usePersistentEvent(channel, { event, agent: user }, [user]);
};

type Viewer = IUser & { viewedAt: Date };
type ViewEvent<T extends string> = { event: T; agent: IUser };

export const useViewerListener = <T extends string = string>(topic: string, event: T) => {
    const [records, setRecords] = React.useState<Record<string, Viewer>>({});
    const user = useUser();

    useSubscription<ViewEvent<T>>(
        topic,
        ({ agent, event: _event }) => {
            if (agent && agent.username !== user?.username && _event === event) {
                setRecords((prevValue) => {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    if (prevValue[agent.email!]) {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        prevValue[agent.email].viewedAt = new Date();
                    } else {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        prevValue[agent.email] = { ...agent, viewedAt: new Date() };
                    }
                    return { ...prevValue };
                });
            }
        },
        undefined,
        [user],
    );

    React.useEffect(() => {
        const interval = setInterval(() => {
            setRecords((prevValue) =>
                Object.fromEntries(
                    Object.entries(prevValue).filter(([_, value]) => Date.now() - value.viewedAt.getTime()),
                ),
            );
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    React.useEffect(() => setRecords({}), [topic]);

    return Object.values(records);
};
